import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const PRDDocuments = () => {
  const [activeTab, setActiveTab] = useState('summary');

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-6 max-w-7xl">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#112d56] mb-4">
            VioConcierge Dedicated Website - PRD Documents
          </h1>
          <p className="text-xl text-gray-600">
            Product Requirements Documents for the standalone VioConcierge website
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="flex bg-white rounded-lg shadow-lg">
            <Button
              className={`px-6 py-3 rounded-l-lg ${activeTab === 'summary' ? 'bg-[#112d56] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              onClick={() => setActiveTab('summary')}
            >
              Executive Summary PRD
            </Button>
            <Button
              className={`px-6 py-3 rounded-r-lg ${activeTab === 'detailed' ? 'bg-[#112d56] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              onClick={() => setActiveTab('detailed')}
            >
              Detailed Technical PRD
            </Button>
          </div>
        </div>

        {/* Executive Summary PRD */}
        {activeTab === 'summary' && (
          <Card className="mb-12 shadow-lg">
            <CardHeader className="bg-[#112d56] text-white">
              <CardTitle className="text-2xl">VioConcierge Dedicated Website - Executive Summary PRD</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="prose max-w-none space-y-6">
                
                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">🎯 Project Overview</h2>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p><strong>Product:</strong> VioConcierge Dedicated Website</p>
                    <p><strong>Company:</strong> Smart AI Solutions</p>
                    <p><strong>Product Category:</strong> Premium Outbound Appointment Confirmation System</p>
                    <p><strong>Target Audience:</strong> Forward-thinking, premium appointment-based businesses</p>
                    <p><strong>Brand Positioning:</strong> "Not every business is ready. The ones who are, lead the way."</p>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">📋 Website Structure</h2>
                  <h3 className="text-xl font-semibold mb-3">Required Pages:</h3>
                  <ol className="list-decimal ml-6 space-y-2">
                    <li><strong>Homepage</strong> - Premium VioConcierge showcase</li>
                    <li><strong>About Us</strong> - Smart AI Solutions company information</li>
                    <li><strong>Features</strong> - Detailed VioConcierge functionality</li>
                    <li><strong>Use Cases</strong> - Premium business scenarios</li>
                    <li><strong>Contact</strong> - Executive consultation booking</li>
                    <li><strong>Privacy Policy</strong> - Adapted from Smart AI Solutions</li>
                    <li><strong>Cookie Policy</strong> - Adapted from Smart AI Solutions</li>
                  </ol>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">🏆 Current Content Analysis</h2>
                  <h3 className="text-xl font-semibold mb-3">Homepage Content (From Current VioConcierge Page):</h3>
                  
                  <h4 className="text-lg font-semibold text-green-600 mb-3">✅ Available Sections:</h4>
                  <ol className="list-decimal ml-6 space-y-1">
                    <li><strong>Hero Section:</strong> "VioConcierge: The Executive Standard for Appointment Confirmations"</li>
                    <li><strong>Video Section:</strong> "Where Efficiency Meets Prestige"</li>
                    <li><strong>Assessment Section:</strong> VioConcierge Appointment Efficiency Assessment (4 cards)</li>
                    <li><strong>Target Businesses:</strong> 6 premium business types (Executive Medical, Elite Professional, etc.)</li>
                    <li><strong>Core Features:</strong> 4 key capabilities (Conversational AI, Follow-up System, Calendar Sync, Analytics)</li>
                    <li><strong>Booking Pipeline:</strong> Cal.com and Calendly integrations</li>
                    <li><strong>Business Impact:</strong> Statistics (£18,000+ revenue loss, 2-4h daily time, 78% voice response, 23% efficiency loss)</li>
                    <li><strong>Industry Benchmarks:</strong> 4 operational metrics (10-15% no-show rate, 40M minutes daily, 8.1 minutes wasted, 130h lost annually)</li>
                    <li><strong>Premium Consultation:</strong> Executive consultation booking with 4 benefit cards</li>
                  </ol>

                  <h4 className="text-lg font-semibold text-green-600 mb-3 mt-6">✅ Premium Messaging Elements:</h4>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>"Not every business is ready. The ones who are, lead the way."</li>
                    <li>"Where efficiency meets prestige"</li>
                    <li>"Most appointment businesses still rely on email or SMS. Leaders don't."</li>
                    <li>"This isn't for everyone — it's for the innovators."</li>
                    <li>"Like a luxury hotel concierge, but for your business."</li>
                    <li>"VioConcierge isn't a tool. It's a statement."</li>
                    <li>"The quiet advantage your competitors don't have."</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">📊 Content Gaps to Fill</h2>
                  
                  <h3 className="text-xl font-semibold mb-3">1. About Us Page Content Needed:</h3>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Smart AI Solutions company overview and mission</li>
                    <li>Leadership team and expertise</li>
                    <li>Commitment to premium solutions</li>
                    <li>VioConcierge as flagship product</li>
                    <li>Company values and innovation</li>
                    <li>Contact information and locations</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3 mt-6">2. Features Page Content Needed:</h3>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Advanced Conversational AI with Call Outcome Intelligence</li>
                    <li>Fully Automated Follow-Up System</li>
                    <li>Real-Time Calendar Sync with Automatic Rescheduling</li>
                    <li>Real-Time Performance Analytics Dashboard</li>
                    <li>Enterprise Integration Capabilities</li>
                    <li>Security and Compliance Features</li>
                    <li>Customization and White-Label Options</li>
                    <li>Advanced Reporting and ROI Tracking</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3 mt-6">3. Use Cases Page Content Needed:</h3>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Executive Medical Practices</li>
                    <li>Elite Professional Services (Legal, Consulting, Financial)</li>
                    <li>Luxury Beauty & Wellness</li>
                    <li>Premium Service Companies</li>
                    <li>Executive Consultancy</li>
                    <li>Exclusive Hospitality</li>
                  </ul>
                  <p className="mt-3"><strong>For Each Use Case:</strong> Business challenge, VioConcierge solution, Premium outcome, ROI metrics, Success story</p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">🚀 Implementation Roadmap</h2>
                  
                  <h3 className="text-xl font-semibold mb-3">Phase 1: Foundation</h3>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Homepage with premium messaging ✅</li>
                    <li>About Us page (Smart AI Solutions)</li>
                    <li>Basic navigation and branding</li>
                    <li>Contact page with consultation form</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3 mt-4">Phase 2: Content Expansion</h3>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Features page with detailed capabilities</li>
                    <li>Use Cases page with premium scenarios</li>
                    <li>Privacy and Cookie policies (adapted)</li>
                    <li>SEO optimization</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3 mt-4">Phase 3: Premium Enhancement</h3>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Advanced assessment tool integration</li>
                    <li>Executive consultation scheduling</li>
                    <li>Premium testimonials and case studies</li>
                    <li>Performance analytics and tracking</li>
                  </ul>
                </div>

              </div>
            </CardContent>
          </Card>
        )}

        {/* Detailed Technical PRD */}
        {activeTab === 'detailed' && (
          <Card className="mb-12 shadow-lg">
            <CardHeader className="bg-[#ff7a1f] text-white">
              <CardTitle className="text-2xl">VioConcierge Dedicated Website - Detailed Technical PRD</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="prose max-w-none space-y-6">
                
                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">Executive Summary</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Create a standalone, premium website for VioConcierge - the executive standard for appointment confirmations. 
                    This website will position VioConcierge as an exclusive, high-end solution for businesses that want to elevate 
                    their client experience and reputation through sophisticated outbound appointment confirmations.
                  </p>
                  <div className="bg-orange-50 p-4 rounded-lg mt-4">
                    <p className="font-semibold text-[#112d56]">Key Positioning: "Not every business is ready. The ones who are, lead the way."</p>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">Website Structure & Pages</h2>
                  
                  <h3 className="text-xl font-semibold mb-3">1. Homepage</h3>
                  <p className="text-gray-600 mb-2"><em>Based on current VioConcierge page content</em></p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p><strong>Hero Section:</strong></p>
                    <ul className="list-disc ml-6 mt-2">
                      <li>Headline: "VioConcierge: The Executive Standard for Appointment Confirmations"</li>
                      <li>Tagline: "Not every business is ready. The ones who are, lead the way."</li>
                      <li>CTA: "Experience Executive-Level Service"</li>
                    </ul>
                    <p className="mt-4"><strong>Key Sections:</strong></p>
                    <ul className="list-disc ml-6 mt-2 space-y-1">
                      <li>Premium video showcase: "Where Efficiency Meets Prestige"</li>
                      <li>Appointment Efficiency Assessment section</li>
                      <li>Forward-thinking businesses showcase (6 premium business types)</li>
                      <li>How VioConcierge transforms your business (4 core features)</li>
                      <li>Automated booking pipeline (Cal.com/Calendly integrations)</li>
                      <li>Business impact statistics (£18,000+ revenue loss, etc.)</li>
                      <li>Operational metrics & industry benchmarks</li>
                      <li>Premium consultation invitation</li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 mt-6">2. About Us Page</h3>
                  <p className="text-gray-600 mb-2"><em>About Smart AI Solutions as parent company</em></p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="list-disc ml-6 space-y-1">
                      <li>Smart AI Solutions company overview</li>
                      <li>Mission and vision</li>
                      <li>Leadership team</li>
                      <li>Company values and commitment to premium solutions</li>
                      <li>VioConcierge as flagship appointment management solution</li>
                      <li>Contact information and company details</li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 mt-6">3. Features Page</h3>
                  <p className="text-gray-600 mb-2"><em>Detailed VioConcierge functionality</em></p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p><strong>Core Features to Highlight:</strong></p>
                    <ul className="list-disc ml-6 mt-2 space-y-1">
                      <li>Advanced Conversational AI with Call Outcome Intelligence</li>
                      <li>Fully Automated Follow-Up System</li>
                      <li>Real-Time Calendar Sync with Automatic Rescheduling</li>
                      <li>Real-Time Performance Analytics Dashboard</li>
                      <li>Multi-platform integrations (Cal.com, Calendly)</li>
                      <li>Enterprise-grade security and compliance</li>
                      <li>White-label customization options</li>
                      <li>Advanced reporting and ROI tracking</li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 mt-6">4. Use Cases Page</h3>
                  <p className="text-gray-600 mb-2"><em>Premium business scenarios</em></p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p><strong>Target Segments:</strong></p>
                    <ul className="list-disc ml-6 mt-2 space-y-1">
                      <li>Executive Medical Practices</li>
                      <li>Elite Professional Services (Legal, Consulting, Financial)</li>
                      <li>Luxury Beauty & Wellness</li>
                      <li>Premium Service Companies</li>
                      <li>Executive Consultancy</li>
                      <li>Exclusive Hospitality (Fine dining, luxury venues)</li>
                    </ul>
                    <p className="mt-4"><strong>Use Case Structure for Each:</strong></p>
                    <ul className="list-disc ml-6 mt-2">
                      <li>Business challenge</li>
                      <li>VioConcierge solution</li>
                      <li>Premium outcome</li>
                      <li>ROI metrics</li>
                      <li>Success story/testimonial</li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 mt-6">5. Contact Page</h3>
                  <p className="text-gray-600 mb-2"><em>Premium consultation booking</em></p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p><strong>Contact Methods:</strong></p>
                    <ul className="list-disc ml-6 mt-2 space-y-1">
                      <li>Premium consultation booking form</li>
                      <li>Direct contact: contact@vioconcierge.com</li>
                      <li>Smart AI Solutions company information</li>
                      <li>Office locations</li>
                      <li>Executive team contact options</li>
                    </ul>
                    <p className="mt-4"><strong>Contact Form Fields:</strong></p>
                    <ul className="list-disc ml-6 mt-2 space-y-1">
                      <li>Business name</li>
                      <li>Contact person</li>
                      <li>Email</li>
                      <li>Phone</li>
                      <li>Business type (dropdown with premium segments)</li>
                      <li>Current appointment volume</li>
                      <li>Biggest appointment management challenge</li>
                      <li>Preferred consultation time</li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 mt-6">6. Privacy Policy Page</h3>
                  <p className="text-gray-600 mb-2"><em>Adapted from Smart AI Solutions</em></p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="list-disc ml-6 space-y-1">
                      <li>Data collection specific to appointment confirmations</li>
                      <li>Voice data processing and storage</li>
                      <li>Client appointment data handling</li>
                      <li>GDPR compliance for voice AI</li>
                      <li>Data retention policies</li>
                      <li>User rights and preferences</li>
                      <li>Smart AI Solutions as data controller</li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 mt-6">7. Cookie Policy Page</h3>
                  <p className="text-gray-600 mb-2"><em>Adapted from Smart AI Solutions</em></p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="list-disc ml-6 space-y-1">
                      <li>Essential cookies for website functionality</li>
                      <li>Analytics cookies for user behavior</li>
                      <li>Marketing cookies for targeting</li>
                      <li>Preference cookies for customization</li>
                      <li>Third-party integrations (Cal.com, Calendly)</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">🎨 Design & Brand Guidelines</h2>
                  
                  <h3 className="text-xl font-semibold mb-3">Visual Identity:</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="list-disc ml-6 space-y-1">
                      <li><strong>Primary Colors:</strong> Navy (#112d56) and Orange (#ff7a1f)</li>
                      <li><strong>Typography:</strong> Clean, executive-level fonts</li>
                      <li><strong>Layout:</strong> Premium, spacious design with ample white space</li>
                      <li><strong>Imagery:</strong> High-quality, professional photography</li>
                      <li><strong>Icons:</strong> Sophisticated, minimal icon design</li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 mt-6">Brand Voice:</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="list-disc ml-6 space-y-1">
                      <li><strong>Exclusive:</strong> Premium positioning for forward-thinking businesses</li>
                      <li><strong>Sophisticated:</strong> Executive-level language and presentation</li>
                      <li><strong>Confident:</strong> Proven results and premium outcomes</li>
                      <li><strong>Prestigious:</strong> Status symbol for modern businesses</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">📋 Next Steps</h2>
                  <ol className="list-decimal ml-6 space-y-2">
                    <li><strong>Review and approve</strong> this PRD structure</li>
                    <li><strong>Create About Us page</strong> content for Smart AI Solutions</li>
                    <li><strong>Develop Features page</strong> with detailed VioConcierge capabilities</li>
                    <li><strong>Build Use Cases page</strong> with premium business scenarios</li>
                    <li><strong>Adapt Privacy/Cookie policies</strong> for VioConcierge context</li>
                    <li><strong>Create premium Contact page</strong> with consultation booking</li>
                    <li><strong>Implement navigation</strong> and cross-page connectivity</li>
                    <li><strong>Test and optimize</strong> for premium user experience</li>
                  </ol>
                </div>

              </div>
            </CardContent>
          </Card>
        )}

        {/* Additional Detailed Technical Specifications */}
        {activeTab === 'detailed' && (
          <Card className="mb-12 shadow-lg">
            <CardHeader className="bg-[#112d56] text-white">
              <CardTitle className="text-2xl">Technical Specifications & Implementation</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="prose max-w-none space-y-6">
                
                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">🔧 Technical Specifications</h2>
                  
                  <h3 className="text-xl font-semibold mb-3">Technology Stack:</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="list-disc ml-6 space-y-1">
                      <li><strong>Frontend:</strong> React.js with modern UI components</li>
                      <li><strong>Styling:</strong> Tailwind CSS for premium design</li>
                      <li><strong>Performance:</strong> Optimized for fast loading and smooth interactions</li>
                      <li><strong>SEO:</strong> Comprehensive optimization for appointment management keywords</li>
                      <li><strong>Analytics:</strong> Google Analytics and conversion tracking</li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 mt-6">Integrations:</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="list-disc ml-6 space-y-1">
                      <li><strong>Booking:</strong> Cal.com integration (cal.com/smartaisolutions/vioconcierge)</li>
                      <li><strong>Assessment:</strong> External assessment tool (assessment.vioconcierge.com)</li>
                      <li><strong>Video:</strong> Vadoo.tv iframe integration (ID: eFjiVTXPnvtStE5w2rmLJSQXXNhvZecM)</li>
                      <li><strong>Contact:</strong> Email integration for consultation requests</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">🎯 Content Strategy</h2>
                  
                  <h3 className="text-xl font-semibold mb-3">SEO Keywords:</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="list-disc ml-6 space-y-1">
                      <li><strong>Primary:</strong> "appointment confirmation system", "outbound appointment confirmations"</li>
                      <li><strong>Secondary:</strong> "premium appointment management", "executive appointment service"</li>
                      <li><strong>Long-tail:</strong> "voice AI appointment confirmations for premium businesses"</li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 mt-6">Content Pillars:</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ol className="list-decimal ml-6 space-y-1">
                      <li><strong>Premium Positioning</strong> - Exclusive, executive-level service</li>
                      <li><strong>Outbound Focus</strong> - Proactive appointment confirmations</li>
                      <li><strong>Business Elevation</strong> - Brand reputation and prestige enhancement</li>
                      <li><strong>Proven Results</strong> - ROI metrics and business impact</li>
                    </ol>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">📈 Success Metrics</h2>
                  
                  <h3 className="text-xl font-semibold mb-3">Business Goals:</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="list-disc ml-6 space-y-1">
                      <li>Position VioConcierge as premium market leader</li>
                      <li>Increase qualified demo bookings from high-value prospects</li>
                      <li>Enhance brand perception and market positioning</li>
                      <li>Generate higher-value leads and conversions</li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 mt-6">User Experience Goals:</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="list-disc ml-6 space-y-1">
                      <li>Communicate exclusivity and premium positioning</li>
                      <li>Clear understanding of outbound confirmation focus</li>
                      <li>Seamless demo booking and consultation process</li>
                      <li>Mobile-optimized premium experience</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">Premium Messaging Framework</h2>
                  
                  <h3 className="text-xl font-semibold mb-3">Core Taglines:</h3>
                  <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-[#ff7a1f]">
                    <ul className="space-y-2">
                      <li>"Not every business is ready. The ones who are, lead the way."</li>
                      <li>"VioConcierge: The executive standard for client calls."</li>
                      <li>"Appointment handling, redefined for forward-thinking businesses."</li>
                      <li>"Where efficiency meets prestige."</li>
                      <li>"The quiet advantage your competitors don't have."</li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 mt-6">Messaging Pillars:</h3>
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-[#112d56] mb-2">1. Exclusivity & Elevation</h4>
                      <ul className="list-disc ml-6">
                        <li>"Most businesses still rely on voicemail. Leaders don't."</li>
                        <li>"This isn't for everyone — it's for the innovators."</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-[#112d56] mb-2">2. Executive-Level Experience</h4>
                      <ul className="list-disc ml-6">
                        <li>"Your brand deserves a concierge, not an answering machine."</li>
                        <li>"Always on. Always professional. Always elevating your reputation."</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-[#112d56] mb-2">3. Prestige in Practice</h4>
                      <ul className="list-disc ml-6">
                        <li>"Like a luxury hotel concierge, but for your business."</li>
                        <li>"Subtle, seamless, and always working in your favour."</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-[#112d56] mb-4">🚀 Implementation Timeline</h2>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-[#112d56] mb-2">Phase 1: Foundation (Week 1)</h4>
                      <ul className="text-sm space-y-1">
                        <li>Homepage development</li>
                        <li>About Us page creation</li>
                        <li>Basic navigation</li>
                        <li>Contact page</li>
                      </ul>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-[#112d56] mb-2">Phase 2: Content (Week 2)</h4>
                      <ul className="text-sm space-y-1">
                        <li>Features page</li>
                        <li>Use Cases page</li>
                        <li>Privacy/Cookie policies</li>
                        <li>SEO optimization</li>
                      </ul>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-[#112d56] mb-2">Phase 3: Enhancement (Week 3)</h4>
                      <ul className="text-sm space-y-1">
                        <li>Analytics implementation</li>
                        <li>Assessment tool integration</li>
                        <li>A/B testing setup</li>
                        <li>Performance optimization</li>
                      </ul>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-[#112d56] mb-2">Phase 4: Launch (Week 4)</h4>
                      <ul className="text-sm space-y-1">
                        <li>Final testing and QA</li>
                        <li>Domain setup</li>
                        <li>Marketing coordination</li>
                        <li>Go-live and monitoring</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-[#112d56] text-white p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-4">📋 Ready to Proceed?</h3>
                  <p className="text-blue-100 mb-4">
                    This PRD provides a comprehensive roadmap for creating a premium, standalone VioConcierge website 
                    that positions the product as an exclusive, executive-level appointment confirmation solution.
                  </p>
                  <p className="text-[#ff7a1f] font-semibold">
                    Next step: Review and approve to begin development of the dedicated VioConcierge website.
                  </p>
                </div>

              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
};

export default PRDDocuments;